import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/core';
import { FieldArray, useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { TabForm } from '../../../../common/components/Tabs';
import { FormSection, FormSubmitButton } from '../../../../common/components/Form/Form';
import { DateInput } from '../../../../common/components/Form/FormDateInput';
import {
  RadioInput,
  RadioGroup,
  CheckBoxInput,
  Label,
  TextInput,
} from '../../../../common/components/Form/FormInputs';
import { traverseObject } from '../../../../common/utils';
import { isQuoteVersionFilled } from './utils';
import {
  QuoteUploadFilename,
  QuoteUploadWrapper,
} from '../../../../common/components/QuoteUpload/QuoteUpload';
import { PrimaryButton, SecondaryButton } from '../../../../common/components/Buttons';
import { AiAnalysisPanel } from './AiAnalysisPanel';
import { QuoteAutoFollowupInput, QuoteAutoFollowupHelp } from './BlocFTabSummary';
import Can from '../../../../roleBasedAccess/Can';
import { aiAnalysisActions } from '../../../../redux/blocs/blocF/aiAnalysisActions';

const ACTIVE_AI_ANALYSIS_STATUSES = ['pending', 'processing'];
const AI_ANALYSIS_POLLING_MAX_ATTEMPTS = 32;
const AI_ANALYSIS_POLLING_FAST_ATTEMPTS = 24;
const AI_ANALYSIS_POLLING_FAST_DELAY = 5000;
const AI_ANALYSIS_POLLING_MAX_DELAY = 30000;
const aiAnalysisPollingDelay = attempt =>
  attempt < AI_ANALYSIS_POLLING_FAST_ATTEMPTS
    ? AI_ANALYSIS_POLLING_FAST_DELAY
    : AI_ANALYSIS_POLLING_MAX_DELAY;

export const BlocTabForm = ({
  folder,
  pending,
  disabled,
  fieldPrefix,
  quoteVersions,
  quoteGroupName,
  ...props
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { values, handleSubmit, isSubmitting, setFieldValue } = useFormikContext();
  const autoFollowupName = `${fieldPrefix.split('.companies.')[0]}.quoteAutoFollowupEnabled`;

  const dispatch = useDispatch();
  const aiAnalyses = useSelector(state => state.blocs.aiAnalyses);

  const quoteVersionIds = useMemo(
    () => Array.from(new Set(quoteVersions.map(qv => qv?.id).filter(Boolean))),
    [quoteVersions],
  );
  const [pollingAttempts, setPollingAttempts] = useState({});

  useEffect(() => {
    quoteVersionIds.forEach(quoteVersionId => {
      dispatch(aiAnalysisActions.fetchAnalysis(quoteVersionId));
    });
  }, [dispatch, quoteVersionIds]);

  useEffect(() => {
    let isMounted = true;
    const timers = quoteVersionIds.reduce((currentTimers, quoteVersionId) => {
      const analysis = aiAnalyses[quoteVersionId];
      const attempt = pollingAttempts[quoteVersionId] || 0;
      const shouldPoll =
        ACTIVE_AI_ANALYSIS_STATUSES.includes(analysis?.status) &&
        !analysis?.loading &&
        attempt < AI_ANALYSIS_POLLING_MAX_ATTEMPTS;

      if (shouldPoll) {
        const delay = aiAnalysisPollingDelay(attempt);
        currentTimers.push(
          setTimeout(() => {
            if (!isMounted) return;
            setPollingAttempts(currentAttempts => ({
              ...currentAttempts,
              [quoteVersionId]: (currentAttempts[quoteVersionId] || 0) + 1,
            }));
            dispatch(aiAnalysisActions.fetchAnalysis(quoteVersionId));
          }, delay),
        );
      } else if (
        analysis?.status &&
        !ACTIVE_AI_ANALYSIS_STATUSES.includes(analysis.status) &&
        pollingAttempts[quoteVersionId]
      ) {
        setPollingAttempts(currentAttempts => {
          const nextAttempts = { ...currentAttempts };
          delete nextAttempts[quoteVersionId];
          return nextAttempts;
        });
      }

      return currentTimers;
    }, []);

    return () => {
      isMounted = false;
      timers.forEach(clearTimeout);
    };
  }, [aiAnalyses, dispatch, pollingAttempts, quoteVersionIds]);

  const formQuoteVersions = traverseObject(values, `${fieldPrefix}.quoteVersions`);

  const [selectedQuoteIndex, setSelectedQuoteIndex] = useState(null);
  const [quoteStatus, setquoteStatus] = useState('');
  const [comment, setComment] = useState('');
  const selectedQuoteVersion =
    selectedQuoteIndex === null ? null : quoteVersions[selectedQuoteIndex];

  const currentQuote = useMemo(() => {
    const currentQuoteVersions = [...formQuoteVersions].reverse();
    return currentQuoteVersions.findIndex(quote => quote.quoteReceivedAt);
  }, [formQuoteVersions]);

  const isAddButtonDisabled =
    !isQuoteVersionFilled(quoteVersions[quoteVersions.length - 1]) ||
    formQuoteVersions.length > quoteVersions.length ||
    disabled;

  const openModal = index => {
    setSelectedQuoteIndex(index);
    const isWorksGroup = quoteGroupName === 'works';
    setComment(
      quoteVersions[index]?.comment || (isWorksGroup ? folder?.blocAWorksQuoteComment || '' : ''),
    );
    setquoteStatus(
      quoteVersions[index]?.quoteStatus ||
        (isWorksGroup ? folder?.blocAWorksQuoteStatus || '' : ''),
    );
    onOpen();
  };

  const handleFinalize = async () => {
    if (selectedQuoteIndex !== null) {
      const updates = [
        setFieldValue(
          `${fieldPrefix}.quoteVersions.${selectedQuoteIndex}.quoteStatus`,
          quoteStatus,
        ),
        setFieldValue(`${fieldPrefix}.quoteVersions.${selectedQuoteIndex}.comment`, comment),
      ];
      if (quoteStatus === 'additional_request') {
        updates.push(
          setFieldValue(
            `${fieldPrefix}.quoteVersions.${selectedQuoteIndex}.additionalRequestsSentAt`,
            new Date(),
          ),
        );
      }
      await Promise.all(updates);
    }

    handleSubmit();

    onClose();
  };

  const isFinalizeButtonEnabled = quoteStatus === 'valid' || comment.length >= 10;

  return (
    <TabForm mx="0" ml={5} maxWidth="none" onSubmit={handleSubmit} {...props}>
      <Stack spacing="3.5rem">
        <FormSection title="Suivi entreprise">
          <Text mb={5}>
            <Text as="span" fontWeight="semibold" mr={1}>
              Date de positionnement :
            </Text>
            {traverseObject(values, `${fieldPrefix}.positionedAt`) || '-'}
          </Text>
          <DateInput
            label="Date de visite"
            name={`${fieldPrefix}.visitAt`}
            id={`${fieldPrefix}.visitAt`}
            disabled={disabled}
          />
        </FormSection>
        <FormSection title="Reception des devis">
          <Box mb={8}>
            <Can
              perform="folder:bloc_f:quote_auto_followup:edit"
              yes={() => (
                <QuoteAutoFollowupInput
                  name={autoFollowupName}
                  label="Suivi automatique des devis"
                />
              )}
              no={() => (
                <QuoteAutoFollowupInput
                  name={autoFollowupName}
                  label="Suivi automatique des devis"
                  disabled
                />
              )}
            />
            <QuoteAutoFollowupHelp />
          </Box>
          <CheckBoxInput mb={5} id={`${fieldPrefix}.noFollowup`} name={`${fieldPrefix}.noFollowup`}>
            Proposition sans suite
          </CheckBoxInput>

          <FieldArray
            name={`${fieldPrefix}.quoteVersions`}
            render={arrayHelpers => (
              <>
                <Button
                  color="coral.900"
                  variantColor={null}
                  leftIcon="plus"
                  mb={6}
                  bg="select.bg.isSelected"
                  _hover={{ bg: 'select.bg.isSelected' }}
                  _focus={{ bg: 'select.bg.isSelected' }}
                  disabled={isAddButtonDisabled}
                  onClick={() =>
                    arrayHelpers.push({
                      quoteReceivedAt: null,
                      additionalRequestsSentAt: null,
                      quoteStatus: null,
                    })
                  }
                >
                  Ajouter un nouveau devis
                </Button>
                <Stack spacing={6} minW="100%">
                  {[...formQuoteVersions].reverse().map((quote, mapIndex) => {
                    const index = formQuoteVersions.length - mapIndex - 1;

                    const isCurrentQuote = currentQuote === mapIndex;
                    const isQuoteVersionDisabled = disabled;
                    const isQuoteVersionReadOnly = isQuoteVersionFilled(quoteVersions[index]);
                    const quoteVersionId = quoteVersions[index]?.id;
                    const quoteStatusValue = quote.quoteStatus || quoteVersions[index]?.quoteStatus;
                    const analysis = aiAnalyses[quoteVersionId];
                    const hasPollingTimedOut =
                      ACTIVE_AI_ANALYSIS_STATUSES.includes(analysis?.status) &&
                      (pollingAttempts[quoteVersionId] || 0) >= AI_ANALYSIS_POLLING_MAX_ATTEMPTS;

                    return (
                      <Box
                        key={index}
                        bg="lightGray4"
                        rounded="15px"
                        p={6}
                        shadow={isCurrentQuote ? 'sm' : null}
                        border={isCurrentQuote ? '1px solid' : null}
                        borderColor="lightGray2"
                        opacity={isQuoteVersionReadOnly && !isCurrentQuote ? 0.8 : null}
                      >
                        <Text color="lightGray2" fontWeight="bold" mb={5}>
                          Devis n°{index + 1}
                        </Text>
                        <Box mb={6}>
                          <Label>Devis</Label>
                          <QuoteUploadWrapper bg="none" p={0}>
                            <QuoteUploadFilename
                              quoteVersion={quote}
                              placeholder="Aucun devis disponible"
                            />
                          </QuoteUploadWrapper>
                        </Box>
                        <TextInput
                          name={`${fieldPrefix}.quoteVersions.${index}.quoteStatus`}
                          display="none"
                        />
                        <TextInput
                          name={`${fieldPrefix}.quoteVersions.${index}.comment`}
                          display="none"
                        />
                        <DateInput
                          label="Date de réception du devis"
                          name={`${fieldPrefix}.quoteVersions.${index}.quoteReceivedAt`}
                          id={`${fieldPrefix}.quoteVersions.${index}.quoteReceivedAt`}
                          disabled={isQuoteVersionDisabled}
                          readOnly={
                            (quoteVersions[index]?.quoteReceivedAt || isQuoteVersionReadOnly) &&
                            !isQuoteVersionDisabled
                          }
                        />

                        <DateInput
                          label="Date de demandes complémentaires"
                          name={`${fieldPrefix}.quoteVersions.${index}.additionalRequestsSentAt`}
                          id={`${fieldPrefix}.quoteVersions.${index}.additionalRequestsSentAt`}
                          style={{ mb: 6 }}
                          disabled={quoteStatus !== 'additional_request'}
                        />
                        {!quoteStatusValue && (
                          <PrimaryButton
                            display="block"
                            mx="auto"
                            disabled={!quote.quoteReceivedAt}
                            onClick={() => openModal(index)}
                          >
                            Analyser le devis
                          </PrimaryButton>
                        )}
                        <AiAnalysisPanel
                          analysis={
                            hasPollingTimedOut ? { ...analysis, pollingTimedOut: true } : analysis
                          }
                          onLaunchOrRetry={() =>
                            dispatch(aiAnalysisActions.retryAnalysis(quoteVersionId))
                          }
                          onReviewSave={review =>
                            dispatch(aiAnalysisActions.saveReview(quoteVersionId, review))
                          }
                          onConfirm={() => {
                            const quoteStatusPath = `${fieldPrefix}.quoteVersions.${index}.quoteStatus`;
                            return dispatch(
                              aiAnalysisActions.confirmAnalysis(quoteVersionId),
                            ).then(() => setFieldValue(quoteStatusPath, 'valid'));
                          }}
                          isConfirmed={quoteStatusValue === 'valid'}
                          isFinalized={Boolean(quoteStatusValue)}
                          isAutoFollowupDisabled={
                            traverseObject(values, autoFollowupName) === false
                          }
                          onSendModificationRequest={message => {
                            setComment(message);
                            setquoteStatus('additional_request');
                            setSelectedQuoteIndex(index);
                            onOpen();
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </>
            )}
          />
        </FormSection>
      </Stack>

      <FormSubmitButton
        type="submit"
        disabled={isSubmitting || pending}
        isLoading={isSubmitting}
        loadingText="Enregistrer"
      >
        Enregistrer
      </FormSubmitButton>
      <Modal isOpen={isOpen} closeOnOverlayClick={false} size="full">
        <ModalOverlay />
        <ModalContent rounded="lg" overflow="hidden" width="90%" height="90%" my="5vh">
          <>
            <ModalHeader as="div" bg="darkGray" color="white">
              <Heading as="h2" fontSize="xl" fontWeight="bold">
                <Text
                  as="span"
                  fontFamily="heading"
                  opacity="0.5"
                  textTransform="uppercase"
                  fontWeight="600"
                >
                  Dossier n°{' '}
                </Text>
                {folder?.formattedId}
                <Text as="span" d="inline-block" mx={2} opacity="0.65">
                  -
                </Text>{' '}
                <Text mt={2} as="small">
                  <Text as="span" fontWeight="semibold" textTransform="uppercase">
                    {folder?.recipient?.lastName || '-'}
                  </Text>{' '}
                  {folder?.recipient?.firstName || '-'}
                </Text>
              </Heading>
            </ModalHeader>
            <ModalCloseButton color="white" _focus={{ shadow: 'none' }} />
            <ModalBody p={5} display="grid" gridTemplateColumns="65% 35%" style={{ gap: '1rem' }}>
              {selectedQuoteVersion?.fileData ? (
                <Box
                  as="iframe"
                  title="Aperçu du devis"
                  src={selectedQuoteVersion.fileData}
                  width="100%"
                  height="100%"
                  border="1px solid"
                  borderColor="placeholder"
                  rounded="lg"
                >
                  <Text as="span" d="block" mt={5} textAlign="center">
                    Votre navigateur ne supporte pas l'affichage de fichiers PDF.
                  </Text>
                </Box>
              ) : (
                <Box
                  width="100%"
                  height="100%"
                  border="1px solid"
                  borderColor="placeholder"
                  rounded="lg"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexDir="column"
                  p={6}
                  textAlign="center"
                >
                  <Text color="placeholder" mb={3}>
                    L’aperçu du devis n’est pas disponible dans cette fenêtre.
                  </Text>
                  {selectedQuoteVersion?.downloadLink && (
                    <Link
                      href={selectedQuoteVersion.downloadLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="coral.900"
                      fontWeight={600}
                    >
                      Ouvrir le devis
                    </Link>
                  )}
                </Box>
              )}
              <Box height="100%" display="flex" flexDir="column">
                <Textarea
                  height="100%"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Indiquez ici vos remarques et demandes de modifications éventuelles. Elles seront communiquées telles quelles à l’entreprise."
                  borderColor="lightGray2"
                  disabled={quoteStatus !== 'additional_request'}
                  _focus={{ outline: 0, borderColor: 'coral.900' }}
                  _invalid={{ borderColor: 'errorBorderColor' }}
                  resize="vertical"
                />
                <Box mt={2}>
                  <RadioGroup
                    value={quoteStatus}
                    onChange={e => setquoteStatus(e.target.value)}
                    isInline
                    legend="Statut de l’analyse du devis"
                  >
                    <RadioInput value="valid">Totalement valide</RadioInput>
                    <RadioInput value="additional_request">Demandes complémentaires</RadioInput>
                  </RadioGroup>
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter pt={0}>
              <SecondaryButton type="button" mr={3} onClick={() => onClose()}>
                Annuler
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={() => handleFinalize()}
                disabled={!isFinalizeButtonEnabled}
              >
                Finaliser
              </PrimaryButton>
            </ModalFooter>
          </>
        </ModalContent>
      </Modal>
    </TabForm>
  );
};
